// app/frontend/pages/ArtistBookingPortals.tsx
import React from 'react';
import { Head } from '@inertiajs/react';
import { Container, Title, Text, Image, Accordion, Box, Stack, Divider, AspectRatio } from '@mantine/core';
import Layout from '../components/Layout';
import { bookingPortalData } from '../data/bookingPortalData';
import { useUser } from '../src/context/UserContext';

type NextPageWithLayout = React.FC & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

const ArtistBookingPortals: NextPageWithLayout = () => {
  return (
    <>
      <Head>
        <title>Artist Booking Portals | Where Can We Dance</title>
        <meta property="og:title" content="Artist Booking Portals | Where Can We Dance" />
        <meta
          property="og:description"
          content="Introducing Artist Booking Portals! Our Artist Booking Portal streamlines the communication between artists and organizers, making it easier for artists to share their necessary info with the organizers that book them. From their bios, promo photos/videos, traveler info, preferred airports, accommodation preferences, diet, payment info & more! No more phone/email tag multiple times over for each piece of information. Here's how to artists can setup their FREE portal and start streamlining their booking process!"
        />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/assets/ARTIST_BOOKING_PORTAL_SEO.jpg" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
      </Head>

      <Container size="sm" py="xl">
        {/* Hero Image and Intro */}
        <Stack gap="lg">
          <Image
            src="/assets/ARTIST_BOOKING_PORTAL_HEADER.jpg"
            alt="WCWD Artist Booking Portal"
            radius="md"
            style={{ maxWidth: '100%', height: 'auto' }}
          />

          <Title order={1} fw={700} lh={1.2} className="text-white rubik-header">
            Introducing Artist Booking Portals
          </Title>

          <Text className="text-white">
            Our Artist Booking Portal streamlines the communication between artists and organizers, making it easier for
            artists to share the necessary info with the organizers who hire them.
          </Text>

          <Text fw={700} c="#EA580C">
            From your bios, promo photos/videos, traveler info, preferred airports, accommodation preferences, diet,
            payment info & more!
          </Text>

          <Text className="text-white">
            No more phone/email tag multiple times over for each piece of information. You can setup your{' '}
            <strong>FREE</strong> portal and start streamlining your booking process TODAY!
          </Text>

          {/* YouTube Video Embed */}
          <Box mt="lg">
            <AspectRatio ratio={16 / 9}>
              <iframe
                src="https://www.youtube.com/embed/q__XGwGpTys"
                title="Artist Booking Portal Guide"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px',
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </AspectRatio>
          </Box>
        </Stack>

        <Divider my="xl" />

        {/* FAQ Sections */}
        {bookingPortalData.map((section, sectionIndex) => (
          <Box key={sectionIndex} mb="xl">
            <Title order={2} mt="lg" mb="md" fw={600} className="text-white poppins">
              {section.section}
            </Title>

            <Accordion
              multiple
              defaultValue={[]}
              classNames={{
                control: 'faq-accordion-control',
                content: 'faq-accordion-content',
                item: 'faq-accordion-item',
                chevron: 'faq-accordion-chevron',
              }}
            >
              {section.items.map((item, itemIndex) => (
                <Accordion.Item key={itemIndex} value={`${sectionIndex}-${itemIndex}`}>
                  <Accordion.Control>{item.question}</Accordion.Control>
                  <Accordion.Panel>
                    {typeof item.answer === 'string' ? <Text className="text-white">{item.answer}</Text> : item.answer}
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Box>
        ))}
      </Container>
    </>
  );
};

// Set the layout
ArtistBookingPortals.layout = (page: React.ReactNode) => <Layout>{page}</Layout>;

export default ArtistBookingPortals;
